import React, { ReactNode, useEffect, useState } from 'react';
import { IEvalDetail, IFbClient, IFlagBase } from '@featbit/js-client-sdk';

import { FeatbitProvider } from './context';
import { getFlagsProxy } from './flagsProxy';
import { IFlagSet, ProviderConfig } from './types';

const fetchFlags = async (fbClient: IFbClient) => {
  const evalDetails: IEvalDetail<string>[] = await fbClient.getAllVariations();

  return evalDetails.reduce((acc, { flagKey, value }) => {
    acc[flagKey] = value;

    return acc;
  }, {} as IFlagSet);
};

export async function WithFeatbitProvider({ client: fbClient, bootstrap }: ProviderConfig) {
  let error: Error;
  let fetchedFlags: IFlagSet = {};

  try {
    await fbClient.waitForInitialization();
    fetchedFlags = await fetchFlags(fbClient);
  } catch (e) {
    error = e as Error;
  }

  const bootstrapFlags = (bootstrap || []).reduce<{ [key: string]: string }>((acc, flag: IFlagBase) => {
    acc[flag.id] = flag.variation;

    return acc;
  }, {});

  const Provider = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState(() => ({
      unproxiedFlags: fetchedFlags,
      ...getFlagsProxy(fbClient, bootstrapFlags, fetchedFlags),
      fbClient,
      error,
    }));

    useEffect(() => {
      async function onReady() {
        const unproxiedFlags = await fetchFlags(fbClient);

        setState((prevState) => ({
          ...prevState,
          unproxiedFlags,
          ...getFlagsProxy(fbClient, bootstrapFlags, unproxiedFlags),
        }));
      }

      function onFailed(e: Error) {
        setState((prevState) => ({ ...prevState, error: e }));
      }

      function onUpdate(changedKeys: string[]) {
        const updates: IFlagSet = changedKeys.reduce((acc, key) => {
          acc[key] = fbClient.variation(key, '');

          return acc;
        }, {} as IFlagSet);

        if (Object.keys(updates).length > 0) {
          setState((prevState) => {
            const updatedUnproxiedFlags = { ...prevState.unproxiedFlags, ...updates };

            return {
              ...prevState,
              unproxiedFlags: updatedUnproxiedFlags,
              ...getFlagsProxy(fbClient, bootstrapFlags, updatedUnproxiedFlags),
            };
          });
        }
      }

      fbClient.on('update', onUpdate);

      // Only subscribe to ready and failed if waitForInitialization timed out
      // because we want the introduction of init timeout to be as minimal and backwards
      // compatible as possible.
      if (error?.name.toLowerCase().includes('timeout')) {
        fbClient.on('failed', onFailed);
        fbClient.on('ready', onReady);
      }

      return function cleanup() {
        fbClient.off('update', onUpdate);
        fbClient.off('failed', onFailed);
        fbClient.off('ready', onReady);
      };
    }, []);

    const { unproxiedFlags: _, ...rest } = state;

    return <FeatbitProvider value={rest}>{children}</FeatbitProvider>;
  };

  return Provider;
}
